import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Quote, Star, MapPin, Phone, Mail, Building2, ArrowRight } from "lucide-react";
import { useLang, COMPANY } from "@/lib/i18n";
import { Reveal, SectionLabel } from "@/lib/ui";
import { EUROPE, projectLL } from "@/lib/europeGeo";

/* ---------- Europe Coverage (real geographic SVG map) ---------- */
const SERVED = new Set(["ro", "de", "fr", "it", "es", "at", "pl", "cz", "hu", "dk", "se", "nl", "be", "sk", "si", "hr", "gb", "pt"]);
const HUB = { lon: 25.9, lat: 47.6, city: "Suceava, RO" };
const CITIES = [
  { lon: 13.4, lat: 52.5, city: "Berlin" },
  { lon: 2.35, lat: 48.85, city: "Paris" },
  { lon: 4.9, lat: 52.37, city: "Amsterdam" },
  { lon: 16.37, lat: 48.2, city: "Wien" },
  { lon: 9.19, lat: 45.46, city: "Milano" },
  { lon: 21.0, lat: 52.23, city: "Warszawa" },
  { lon: -3.7, lat: 40.4, city: "Madrid" },
  { lon: 12.57, lat: 55.68, city: "København" },
  { lon: 12.5, lat: 41.9, city: "Roma" },
];
const COUNTRIES = ["Germania", "Franța", "Olanda", "Belgia", "Italia", "Austria", "Polonia", "Spania", "Cehia", "Ungaria", "Danemarca", "Suedia"];

export function Coverage() {
  const { t } = useLang();
  const hub = projectLL(HUB.lon, HUB.lat);
  return (
    <section id="acoperire" className="relative py-24 md:py-32 bg-mesh overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionLabel>{t({ ro: "Acoperire europeană", en: "European coverage" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "27 de țări. Un singur partener.", en: "27 countries. One partner." })}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-slate-400 text-lg max-w-md">
              {t({ ro: "Din inima Bucovinei până în cel mai îndepărtat colț al Europei, conectăm piețe și oameni prin rute frecvente și fiabile.", en: "From the heart of Bukovina to the furthest corner of Europe, we connect markets and people through frequent, reliable routes." })}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2">
              {COUNTRIES.map((c) => (
                <span key={c} className="rounded-full border border-white/10 bg-slate-900/60 px-4 py-1.5 text-sm text-slate-300 hover:border-sky-400/50 hover:text-white transition-colors">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <div className="relative rounded-[2rem] border border-white/10 glass p-4 md:p-6">
            <svg viewBox={EUROPE.viewBox} className="w-full h-auto" data-testid="europe-map">
              <defs>
                <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#233dff" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#233dff" stopOpacity="0" />
                </radialGradient>
                <marker id="arrow" markerWidth="6" markerHeight="6" refX="4.5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#5b6bff" />
                </marker>
              </defs>

              {/* Country shapes */}
              {EUROPE.countries.map((c) => {
                const served = SERVED.has(c.id);
                return (
                  <path
                    key={c.id}
                    d={c.d}
                    fill={served ? "#233dff" : "#131c33"}
                    fillOpacity={served ? 0.9 : 1}
                    stroke={served ? "#5b6bff" : "rgba(255,255,255,0.08)"}
                    strokeWidth={served ? 1.2 : 0.6}
                  />
                );
              })}

              {/* Hub glow */}
              <circle cx={hub.x} cy={hub.y} r="60" fill="url(#hubGlow)" />

              {/* Dashed arrows from Romania */}
              {CITIES.map((c, i) => {
                const p = projectLL(c.lon, c.lat);
                return <line key={i} x1={hub.x} y1={hub.y} x2={p.x} y2={p.y} stroke="#5b6bff" strokeWidth="1.6" strokeOpacity="0.7" markerEnd="url(#arrow)" className="route-dash" />;
              })}

              {/* City dots + labels */}
              {[{ ...HUB, hub: true }, ...CITIES].map((c, i) => {
                const p = projectLL(c.lon, c.lat);
                return (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r={c.hub ? 6 : 4} fill={c.hub ? "#233dff" : "#f8fafc"} stroke="#020617" strokeWidth="1.5" />
                    <text x={p.x + 9} y={p.y + 4} fill={c.hub ? "#ffffff" : "#cbd5e1"} fontSize="13" fontFamily="Manrope" fontWeight={c.hub ? 800 : 600}>{c.city}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
const TESTI = [
  { n: "Andreas M.", r: "Logistics Manager, München", ro: "Punctualitate impecabilă și comunicare constantă. HEB TEAM TRANS a devenit transportatorul nostru de bază pentru rutele din DE.", en: "Impeccable punctuality and constant communication. HEB TEAM TRANS became our go-to carrier for DE routes." },
  { n: "Sofie D.", r: "Supply Chain, Rotterdam", ro: "Marfă frigorifică livrată în condiții perfecte, de fiecare dată. Monitorizarea GPS ne oferă liniște totală.", en: "Refrigerated cargo delivered in perfect condition, every time. GPS tracking gives us complete peace of mind." },
  { n: "Marco R.", r: "Export Dept., Milano", ro: "Profesionalism real, nu doar promisiuni. Recomand cu încredere pentru transport internațional.", en: "Real professionalism, not just promises. I confidently recommend them for international transport." },
  { n: "James H.", r: "Fulfilment Partner, Amazon UK", ro: "Un partener de nădejde pentru livrările noastre din Marea Britanie. Comunicare excelentă, termene respectate întotdeauna și marfă în stare impecabilă la fiecare cursă.", en: "A dependable partner for our UK deliveries. Excellent communication, deadlines always met and cargo in flawless condition on every run." },
];
export function Testimonials() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-32 bg-slate-950">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <SectionLabel>{t({ ro: "Testimoniale", en: "Testimonials" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "Încrederea partenerilor noștri", en: "The trust of our partners" })}
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTI.map((x, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-white/10 bg-slate-900/50 p-8 hover:-translate-y-1 hover:border-sky-400/40 transition-[transform,border-color] duration-300">
                <Quote className="h-8 w-8 text-sky-400/60" />
                <div className="mt-4 flex gap-1">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-sky-400 text-sky-400" />)}</div>
                <p className="mt-4 text-slate-200 leading-relaxed">"{t(x)}"</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="font-semibold">{x.n}</div>
                  <div className="text-sm text-slate-400">{x.r}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
const FAQS = [
  { q_ro: "Ce tipuri de servicii de transport oferiți?", q_en: "What transport services do you offer?", a_ro: "Oferim soluții complete de logistică: transport rutier de mărfuri generale, marfă cu temperatură controlată, curtain side și livrări express în toată Europa.", a_en: "We provide complete logistics solutions: road freight for general goods, temperature-controlled cargo, curtain side and express deliveries across Europe." },
  { q_ro: "În ce țări asigurați transportul?", q_en: "Which countries do you cover?", a_ro: "Conectăm România cu întreaga Uniune Europeană — Germania, Italia, Franța, Olanda, Belgia și încă 22 de țări. Oriunde există un drum, ajungem.", a_en: "We connect Romania with the entire EU — Germany, Italy, France, the Netherlands, Belgium and 22 more countries. Wherever there is a road, we reach it." },
  { q_ro: "Transporturile sunt monitorizate GPS în timp real?", q_en: "Are shipments tracked via GPS in real time?", a_ro: "Absolut. Toate camioanele sunt dotate cu sisteme GPS de ultimă generație, cu actualizări precise despre poziție și ora estimată de livrare.", a_en: "Absolutely. All trucks are equipped with state-of-the-art GPS, with precise updates on position and estimated delivery time." },
  { q_ro: "Marfa este asigurată pe durata transportului?", q_en: "Is cargo insured during transport?", a_ro: "Toate transporturile sunt acoperite de asigurare CMR extinsă, plus protocoale stricte de fixare și manipulare a mărfii.", a_en: "All shipments are covered by extended CMR insurance, plus strict cargo securing and handling protocols." },
  { q_ro: "Cum obțin o ofertă de preț?", q_en: "How do I get a price quote?", a_ro: "Ne contactezi direct prin telefon, WhatsApp sau e-mail. Echipa noastră răspunde rapid cu o soluție adaptată nevoilor tale.", a_en: "Contact us directly by phone, WhatsApp or email. Our team responds quickly with a solution tailored to your needs." },
];
export function Faq() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-32 bg-mesh">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <div className="text-center">
          <SectionLabel>{t({ ro: "Întrebări frecvente", en: "FAQ" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "Întrebări? Răspunsuri.", en: "Questions? Answers." })}
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-white/10">
                <AccordionTrigger className="text-left font-display text-lg font-semibold hover:text-sky-400 hover:no-underline py-5">
                  {t({ ro: f.q_ro, en: f.q_en })}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-base leading-relaxed pb-5">
                  {t({ ro: f.a_ro, en: f.a_en })}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Contact (details only, NO form) ---------- */
export function Contact() {
  const { t } = useLang();
  const cards = [
    { icon: Phone, label: t({ ro: "Telefon", en: "Phone" }), value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, cta: t({ ro: "Sună acum", en: "Call now" }), tid: "contact-phone" },
    { icon: Phone, label: t({ ro: "Telefon birou", en: "Office phone" }), value: COMPANY.phoneOffice, href: `tel:${COMPANY.phoneOfficeRaw}`, cta: t({ ro: "Sună acum", en: "Call now" }), tid: "contact-phone-office" },
    { icon: Mail, label: t({ ro: "E-mail", en: "Email" }), value: COMPANY.email, href: `mailto:${COMPANY.email}`, cta: t({ ro: "Trimite mesaj", en: "Send message" }), tid: "contact-email" },
    { icon: Building2, label: t({ ro: "Adresă", en: "Address" }), value: t(COMPANY.location), href: "https://maps.google.com/?q=Dumbraveni+Suceava", cta: t({ ro: "Vezi pe hartă", en: "View on map" }), tid: "contact-address" },
  ];
  return (
    <section id="contact" className="relative py-24 md:py-32 bg-slate-950">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <SectionLabel>{t({ ro: "Contact", en: "Contact" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter">
              {t({ ro: "Hai să construim un ", en: "Let's build a " })}
              <span className="text-gradient">{t({ ro: "drum comun", en: "common road" })}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-slate-400 text-lg">
              {t({ ro: "Suntem la un apel distanță. Îți oferim stabilitatea și profesionalismul de care afacerea ta are nevoie.", en: "We're just one call away. We offer the stability and professionalism your business needs." })}
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full glass px-5 py-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-blue-600 text-sm font-bold">EH</span>
              <span className="text-sm">
                <span className="text-slate-400">{t({ ro: "Persoană de contact:", en: "Contact person:" })}</span>{" "}
                <strong className="text-white">{COMPANY.person}</strong>
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <a data-testid={c.tid} href={c.href} target={c.icon === Building2 ? "_blank" : undefined} rel="noreferrer"
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-slate-900/50 p-8 hover:-translate-y-1 hover:border-sky-400/40 transition-[transform,border-color] duration-300">
                  <div className="grid h-14 w-14 place-items-center rounded-xl bg-blue-600/15 border border-blue-500/20 group-hover:bg-blue-600 transition-colors">
                    <Icon className="h-6 w-6 text-sky-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="mt-6 text-sm uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="mt-2 font-display text-xl font-bold break-words">{c.value}</div>
                  <div className="mt-auto pt-6 inline-flex items-center gap-2 text-sky-400 font-semibold">
                    {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  const { t } = useLang();
  return (
    <footer className="relative border-t border-white/10 bg-slate-950 py-14">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 flex flex-col items-center text-center gap-6">
        <div className="flex items-center">
          <img src={COMPANY.logo} alt="HEB TEAM TRANS SRL" className="h-11 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        </div>
        <p className="font-display text-lg font-bold text-sky-400 italic tracking-tight">{t(COMPANY.slogan)}</p>
        <p className="max-w-md text-slate-400">
          {t({ ro: "Partenerul tău de încredere pe rutele Europei. Livrăm siguranță, punctualitate și profesionalism la fiecare kilometru.", en: "Your trusted partner on Europe's routes. We deliver safety, punctuality and professionalism at every kilometer." })}
        </p>
        <div className="flex gap-3">
          <a href={`tel:${COMPANY.phoneRaw}`} className="grid h-11 w-11 place-items-center rounded-full glass hover:border-sky-400/50 transition-colors"><Phone className="h-5 w-5" /></a>
          <a href={`mailto:${COMPANY.email}`} className="grid h-11 w-11 place-items-center rounded-full glass hover:border-sky-400/50 transition-colors"><Mail className="h-5 w-5" /></a>
          <a href="https://maps.google.com/?q=Dumbraveni+Suceava" target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center rounded-full glass hover:border-sky-400/50 transition-colors"><MapPin className="h-5 w-5" /></a>
        </div>
        <div className="text-sm text-slate-400">{COMPANY.person} · {COMPANY.phone} · {COMPANY.phoneOffice} · {COMPANY.email} · {t(COMPANY.location)}</div>

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="nofollow noreferrer" className="rounded-lg bg-white p-1.5 hover:-translate-y-0.5 transition-transform">
            <img src="/anpc-sal.webp" alt="Soluționarea Alternativă a Litigiilor" className="h-9 w-auto" />
          </a>
          <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="nofollow noreferrer" className="rounded-lg bg-white p-1.5 hover:-translate-y-0.5 transition-transform">
            <img src="/anpc-sol.webp" alt="Soluționarea Online a Litigiilor" className="h-9 w-auto" />
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-400">
          <Link to="/termeni-si-conditii" className="hover:text-sky-400 transition-colors">{t({ ro: "Termeni și condiții", en: "Terms & conditions" })}</Link>
          <span className="text-slate-700">·</span>
          <Link to="/politica-cookies" className="hover:text-sky-400 transition-colors">{t({ ro: "Politica de cookies", en: "Cookie policy" })}</Link>
        </div>

        <div className="text-sm text-slate-500">© {new Date().getFullYear()} HEB TEAM TRANS SRL · {t({ ro: "Transport internațional de marfă", en: "International freight transport" })}</div>
      </div>
    </footer>
  );
}
